import React from "react";
import {
  Search,
  Plus,
  Loader2,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  FileSpreadsheet,
  Pencil,
} from "lucide-react";
import { Wallet as WalletType } from "../../../../types/features/administration/Wallet";
import { FilterState } from "../../../../types/features/administration/Wallet";

interface WalletsTabProps {
  wallets: WalletType[];
  walletsLoading: boolean;
  walletsPagination: any;
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalWalletPages: number;
  walletSortBy: string;
  walletSortOrder: string;
  handleWalletSort: (sortBy: string) => void;
  showNegativeOnly: boolean;
  setShowNegativeOnly: React.Dispatch<React.SetStateAction<boolean>>;
  exportWalletsToExcel: () => Promise<void>;
  isExportingWallets: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  setRowActionWallet: React.Dispatch<React.SetStateAction<WalletType | null>>;
  setRowActionType: React.Dispatch<React.SetStateAction<"recharge" | "debit">>;
  onEditDailyLimit: (wallet: WalletType) => void;
}

export const WalletsTab: React.FC<WalletsTabProps> = ({
  wallets,
  walletsLoading,
  walletsPagination,
  filters,
  setFilters,
  currentPage,
  setCurrentPage,
  totalWalletPages,
  walletSortBy,
  walletSortOrder,
  handleWalletSort,
  showNegativeOnly,
  setShowNegativeOnly,
  exportWalletsToExcel,
  isExportingWallets,
  setShowCreateModal,
  setRowActionWallet,
  setRowActionType,
  onEditDailyLimit,
}) => {
  const currentWallets = wallets || [];

  return (
    <div>
      {/* Search + Actions */}
      <div className="p-4 flex justify-between items-center border-b border-gray-100">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, pays, devise..."
              value={filters.search}
              onChange={(e) => {
                setFilters({ search: e.target.value });
                setCurrentPage(1);
              }}
              className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="flex gap-2 ml-4">
          <button
            onClick={() => {
              setShowNegativeOnly((prev) => !prev);
              setCurrentPage(1);
            }}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium border ${
              showNegativeOnly
                ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                : "bg-white text-red-600 border-red-300 hover:bg-red-50"
            }`}
            title="Afficher uniquement les wallets à solde négatif"
          >
            <AlertTriangle className="w-4 h-4" />
            <span>{showNegativeOnly ? "Soldes négatifs" : "Voir négatifs"}</span>
          </button>
          <button
            onClick={exportWalletsToExcel}
            disabled={isExportingWallets}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            title="Exporter le tableau des wallets en Excel"
          >
            {isExportingWallets ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            <span>{isExportingWallets ? "Export..." : "Exporter Excel"}</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Créer wallet</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                <button
                  onClick={() => handleWalletSort("name")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Utilisateur
                  {walletSortBy === "name" ? (
                    walletSortOrder === "asc" ? (
                      <ArrowUp className="w-4 h-4" />
                    ) : (
                      <ArrowDown className="w-4 h-4" />
                    )
                  ) : (
                    <ArrowUpDown className="w-4 h-4 opacity-30" />
                  )}
                </button>
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Pays</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Code</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Indicatif</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Devise</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">Total rechargé</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">Total débité</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">
                <button
                  onClick={() => handleWalletSort("balance")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors ml-auto"
                >
                  Solde
                  {walletSortBy === "balance" ? (
                    walletSortOrder === "asc" ? (
                      <ArrowUp className="w-4 h-4" />
                    ) : (
                      <ArrowDown className="w-4 h-4" />
                    )
                  ) : (
                    <ArrowUpDown className="w-4 h-4 opacity-30" />
                  )}
                </button>
              </th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">Limite / jour</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                <button
                  onClick={() => handleWalletSort("created_at")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Mis à jour
                  {walletSortBy === "created_at" ? (
                    walletSortOrder === "asc" ? (
                      <ArrowUp className="w-4 h-4" />
                    ) : (
                      <ArrowDown className="w-4 h-4" />
                    )
                  ) : (
                    <ArrowUpDown className="w-4 h-4 opacity-30" />
                  )}
                </button>
              </th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {walletsLoading ? (
              <tr>
                <td colSpan={11} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-2"></div>
                    <p className="text-gray-600 text-sm">Chargement des wallets...</p>
                  </div>
                </td>
              </tr>
            ) : currentWallets.length > 0 ? (
              currentWallets.map((wallet) => {
                const totalRecharged = wallet.total_recharged ?? 0;
                const totalDebited = wallet.total_debited ?? 0;
                const isOverDebited = totalRecharged < totalDebited;
                return (
                  <tr
                    key={wallet.id}
                    className={
                      isOverDebited ? "bg-red-50 border-l-4 border-red-500 hover:bg-red-100" : "hover:bg-gray-50"
                    }
                    title={isOverDebited ? "Total débité supérieur au total rechargé" : undefined}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">
                          {wallet.user?.first_name} {wallet.user?.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{wallet.user?.email}</p>
                        <p className="text-xs text-gray-400">{wallet.user?.phonenumber}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-800">{wallet.country?.name || "—"}</td>
                    <td className="px-6 py-4 text-gray-600">{wallet.country?.code || "—"}</td>
                    <td className="px-6 py-4 text-gray-600">+{wallet.country?.phonecode || "—"}</td>
                    <td className="px-6 py-4 font-medium text-gray-700">{wallet.country?.devise || "—"}</td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-semibold text-green-600">{totalRecharged.toLocaleString()}</span>
                      <span className="text-xs text-gray-500 ml-1">{wallet.country?.devise}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-semibold text-red-600">{totalDebited.toLocaleString()}</span>
                      <span className="text-xs text-gray-500 ml-1">{wallet.country?.devise}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`font-bold text-lg ${wallet.balance > 0 ? "text-green-600" : "text-gray-400"}`}>
                        {wallet.balance.toLocaleString()}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">{wallet.country?.devise}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => onEditDailyLimit(wallet)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-amber-50 transition-colors"
                        title="Modifier la limite journalière"
                      >
                        <span className="font-semibold text-amber-700">
                          {Number(wallet.daily_limit ?? 0).toLocaleString("fr-FR")}
                        </span>
                        <span className="text-xs text-gray-500">{wallet.country?.devise}</span>
                        <Pencil className="w-3.5 h-3.5 text-amber-600" />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {wallet.updated_at ? new Date(wallet.updated_at).toLocaleString("fr-FR") : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            setRowActionWallet(wallet);
                            setRowActionType("recharge");
                          }}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-xs font-medium"
                          title="Recharger ce wallet"
                        >
                          <ArrowUpCircle className="w-3.5 h-3.5" />
                          Recharger
                        </button>
                        <button
                          onClick={() => {
                            setRowActionWallet(wallet);
                            setRowActionType("debit");
                          }}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-xs font-medium"
                          title="Débiter ce wallet"
                        >
                          <ArrowDownCircle className="w-3.5 h-3.5" />
                          Débiter
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} className="px-6 py-8 text-center text-gray-500">
                  {filters.search ? "Aucun wallet trouvé pour cette recherche" : "Aucun wallet trouvé"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {walletsLoading ? (
            <span className="text-gray-400">Chargement...</span>
          ) : (
            <span>
              Page {currentPage} sur {totalWalletPages} ({walletsPagination?.total || 0} wallets au total)
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1 || walletsLoading}
            className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-100"
          >
            Précédent
          </button>
          <button
            onClick={() => setCurrentPage(Math.min(totalWalletPages, currentPage + 1))}
            disabled={currentPage === totalWalletPages || walletsLoading}
            className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-100"
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
};
